import {
  ContextType,
  UserMessage,
  WebsocketCKDocumentConversationContext,
  WebSocketConversationCommentContextType,
  WebsocketConversationMetaData,
  WebsocketExternalDocumentConversationContext,
  WebsocketStatusUpdateConversationContext,
  WebsocketTaskConversationContext,
} from 'api/conversations';
import { ProjectId } from 'api/models';
import { ProjectGlobalConversation } from 'api/project-conversations';
import {
  ProjectGlobalConversationDocument,
  ProjectGlobalConversationStatusUpdate,
} from 'api/project-conversations/global-conversation';
import { SocketData as ProjectSocketData, AccountSocketData } from 'api/pushes';
import { Task } from 'data/tasks/index';

type SocketData<T> = ProjectSocketData<T> | AccountSocketData<T>;
export interface GlobalConversationQueryResult {
  conversations: ProjectGlobalConversation[];
  unread: number;
}
export const globalConversationQueryKey = {
  default: ['global-conversation'] as Array<string | number>,
  byProjectId(projectId: ProjectId[1]): (string | number)[] {
    return this.default.concat(projectId);
  },
};

const predicateByType = {
  isStatusUpdate: (
    socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
  ): socketData is SocketData<
    UserMessage & WebsocketStatusUpdateConversationContext
  > =>
    socketData.data.commentContext.type ===
      WebSocketConversationCommentContextType.Document &&
    socketData.data.commentContext.parentType === 'STATUS_UPDATE',

  isCkDocument: (
    socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
  ): socketData is SocketData<
    UserMessage & WebsocketCKDocumentConversationContext
  > =>
    socketData.data.commentContext.type ===
      WebSocketConversationCommentContextType.Document &&
    socketData.data.commentContext.parentType ===
      WebSocketConversationCommentContextType.SpaceTab,

  isExternalDocument: (
    socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
  ): socketData is SocketData<
    UserMessage & WebsocketExternalDocumentConversationContext
  > =>
    socketData.data.commentContext.type ===
    WebSocketConversationCommentContextType.SpaceTab,

  isTask: (
    socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
  ): socketData is SocketData<UserMessage & WebsocketTaskConversationContext> =>
    socketData.data.commentContext.type ===
    WebSocketConversationCommentContextType.Task,
};

export const constructGlobalConversation = (
  socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
): ProjectGlobalConversation | undefined => {
  if (
    socketData.data.commentContext.type ===
    WebSocketConversationCommentContextType.ProjectConversation
  ) {
    return undefined;
  }

  if (predicateByType.isTask(socketData)) {
    return {
      contextType: 'TASK',
      displayEntityType: 'tasks',
      entityId: socketData.data.commentContext.id,
      entityName: socketData.data.commentContext.associatedTask.taskName,
      entityType: ContextType.TASK,
      lastCommentCreatedTime: socketData.data.createdAt,
      private: socketData.data.commentContext.associatedTask.private,
      unreadMessages: 0,
    } as ProjectGlobalConversation;
  }
  if (predicateByType.isCkDocument(socketData)) {
    return {
      contextType: 'DOCUMENT',
      displayEntityType: 'space-tabs',
      entityId: socketData.data.commentContext.id,
      entityName: socketData.data.commentContext.associatedDocument.title,
      entityType: ContextType.DOCUMENT,
      lastCommentCreatedTime: socketData.data.createdAt,
      private:
        socketData.data.commentContext.associatedDocument.spaceTab.space
          .private,
      spaceTab: {
        space: {
          spaceId:
            socketData.data.commentContext.associatedDocument.spaceTab.space
              .spaceId,
        },
        spaceTabId:
          socketData.data.commentContext.associatedDocument.spaceTab.spaceTabId,
        metaData: {
          icon: '',
        },
      },
      unreadMessages: 0,
    } as ProjectGlobalConversationDocument;
  }

  if (predicateByType.isExternalDocument(socketData)) {
    return {
      contextType: 'DOCUMENT',
      displayEntityType: 'space-tabs',
      entityId: socketData.data.commentContext.id,
      entityName:
        socketData.data.commentContext.associatedSpaceTab.spaceTabTitle,
      entityType: ContextType.DOCUMENT,
      lastCommentCreatedTime: socketData.data.createdAt,
      private: socketData.data.commentContext.associatedSpaceTab.space.private,
      spaceTab: {
        space: {
          spaceId:
            socketData.data.commentContext.associatedSpaceTab.space.spaceId,
        },
        spaceTabId:
          socketData.data.commentContext.associatedSpaceTab.spaceTabId,
        metaData: {
          icon:
            socketData.data.commentContext.associatedSpaceTab.metaData.icon[0]
              .href ?? '',
        },
      },
      unreadMessages: 0,
    } as ProjectGlobalConversationDocument;
  }

  if (predicateByType.isStatusUpdate(socketData)) {
    return {
      contextType: 'DOCUMENT',
      displayEntityType: 'status-updates',
      entityId: socketData.data.commentContext.id,
      entityName: socketData.data.commentContext.associatedDocument.title,
      entityType: ContextType.DOCUMENT,
      lastCommentCreatedTime: socketData.data.createdAt,
      private: false,
      statusUpdate: {
        statusUpdateId:
          socketData.data.commentContext.associatedDocument.statusUpdate
            .statusUpdateId,
      },
      unreadMessages: 0,
    } as ProjectGlobalConversationStatusUpdate;
  }

  return undefined;
};

export const filterTaskData = (
  snap: SocketData<Task | number[]>,
): SocketData<Task> | undefined => {
  if (Array.isArray(snap.data)) {
    return undefined;
  }

  return snap as SocketData<Task>;
};
